import { TableCell, TableRow } from "@mui/material";
import classNames from "classnames";
import ChannelCategoryIcon from "components/shared/ChannelCategoryIcon";
import { channelCategories } from "containers/Channels/CreateChannelModal";
import { formatNumber, sortByProperty } from "helpers/utils";
import type { ChannelConsolidatedSalesData, ChannelSales } from "store/analyticsStore";

import type { ICategoryTotalValues } from "..";
import styles from "../style.module.scss";

interface ICategoryRow {
    /**
     * Object with data per category.
     * Key is category kind. Values is an array with sales data per channel.
     */
    categoriesData: Record<number, ChannelSales[]> | undefined;
    /**
     * Array with data for categories total amounts.
     */
    totalValues: ICategoryTotalValues[];
}

/**
 * Within TitleConsolidatedSales, row component for each channel category.
 * A row contains data for the category and then subsequent rows for each channel
 * within that category.
 */
const CategoryRow = ({ categoriesData, totalValues }: ICategoryRow) => {
    return (
        categoriesData &&
        Object.entries(categoriesData).map(([kind, channels]) => {
            const kindTotalValues = totalValues.find((item) => item.kind === +kind);
            const sortedChannels: ChannelSales[] = sortByProperty(channels, "data.turnover.N");
            return (
                <>
                    <TableRow key={kind} className={styles["category-row"]}>
                        <TableCell className={styles["name-column"]}>
                            <div>
                                <ChannelCategoryIcon kind={+kind} />
                                {channelCategories().find((item) => item.value === +kind)?.label ?? ""}
                            </div>
                        </TableCell>
                        <TableCell align="right" className={styles["total-value"]}>
                            {formatNumber(kindTotalValues?.turnover ?? 0, { isCurrency: true })}
                        </TableCell>
                        <TableCell align="right" className={styles["total-value"]}>
                            {formatNumber(kindTotalValues?.quantities ?? 0)}
                        </TableCell>
                    </TableRow>
                    {sortedChannels.map((channel, i) => {
                        return (
                            <TableRow
                                className={classNames({
                                    [styles["channel-row"]]: true,
                                    [styles["last"]]: i === channels.length - 1,
                                })}
                                key={`channel-${channel.name}`}
                            >
                                <TableCell className={styles["name-column"]}>{channel.name}</TableCell>
                                <TableCell align="right">
                                    {formatNumber(
                                        (channel.data as ChannelConsolidatedSalesData).total_channel_id_turnover ?? 0,
                                        { isCurrency: true },
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(
                                        (channel.data as ChannelConsolidatedSalesData).total_channel_id_quantities ?? 0,
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </>
            );
        })
    );
};

export default CategoryRow;
